<template>
  <app>
    <!-- Conditionally render the topbar and header if their components are loaded and conditions are met -->
    <component v-if="topbarComponent && !isInIframe && $route.name !== 'login' && $route.name !== 'logout' && $route.name !== 'forgot-password'" :is="topbarComponent" />
    <component v-if="headerComponent && !isInIframe && $route.name !== 'login' && $route.name !== 'logout' && $route.name !== 'forgot-password'" :is="headerComponent" />

    <!-- Render the dynamically loaded component based on the route name -->
    <component :is="currentComponent" />

    <!-- Conditionally render the footer if its component is loaded and conditions are met -->
    <component v-if="footerComponent && !isInIframe && $route.name !== 'login' && $route.name !== 'logout' && $route.name !== 'forgot-password'" :is="footerComponent" />

    <vue3-confirm-dialog></vue3-confirm-dialog>
  </app>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BaseView',
  data() {
    return {
      loggedin: localStorage.getItem('xperttoken') || false,
      isInIframe: false,
      topbarComponent: null,
      headerComponent: null,
      footerComponent: null,
      currentComponent: null, // Holds the dynamically loaded component
    };
  },
  watch: {
    // Watch for route name changes to load the appropriate component
    '$route.name': {
      immediate: true,
      handler(newRouteName) {
        this.loadComponent(newRouteName);
      },
    },
  },
  methods: {
    async loadComponent(routeName) {
      let component = null;

      // Determine which component to load based on the route name
      switch (routeName) {
        // Super Admin Components
        case 'customersadmin':
          component = () => import('../components/customers/admin/CustomersComponent.vue');
          break;
        case 'customersnewadmin':
          component = () => import('../components/customers/admin/CustomerCreateComponent.vue');
          break;
          
        case 'recorddemo':
          component = () => import('../demo/RecordView.vue');
          break;
          
        case 'symbldemo':
          component = () => import('../demo/SymblView.vue');
          break;

        // Admin Components
        case 'bookingsadmin':
          component = () => import('../components/bookings/admin/BookingComponent.vue');
          break;
        case 'bookingsdetailadmin':
          component = () => import('../components/bookings/admin/BookingDetailComponent.vue');
          break;
        case 'assetsadmin':
          component = () => import('../components/assets/admin/AssetsComponent.vue');
          break;
        case 'assetcreateadmin':
          component = () => import('../components/assets/admin/AssetCreateComponent.vue');
          break;
        case 'asseteditadmin':
          component = () => import('../components/assets/admin/AssetEditComponent.vue');
          break;
        case 'categoriesadmin':
          component = () => import('../components/settings/admin/AdminSettingsCategoriesComponent.vue');
          break;
        case 'categorycreateadmin':
          component = () => import('../components/settings/admin/AdminSettingsCategoriesCreateComponent.vue');
          break;
        case 'categoryeditadmin':
          component = () => import('../components/settings/admin/AdminSettingsCategoriesEditComponent.vue');
          break;
        case 'tabsadmin':
          component = () => import('../components/settings/admin/AdminSettingsTabsComponent.vue');
          break;
        case 'tabscreateadmin':
          component = () => import('../components/settings/admin/AdminSettingsTabsCreateComponent.vue');
          break;
        case 'tabseditadmin':
          component = () => import('../components/settings/admin/AdminSettingsTabsEditComponent.vue');
          break;
        case 'fieldscreateadmin':
          component = () => import('../components/settings/admin/AdminSettingsFieldsCreateComponent.vue');
          break;
        case 'fieldseditadmin':
          component = () => import('../components/settings/admin/AdminSettingsFieldsEditComponent.vue');
          break;
        case 'usersadmin':
          component = () => import('../components/settings/admin/AdminSettingsUsersComponent.vue');
          break;
        case 'userscreateadmin':
          component = () => import('../components/settings/admin/AdminSettingsUsersAddComponent.vue');
          break;
        case 'userseditadmin':
          component = () => import('../components/settings/admin/AdminSettingsUsersUpdateComponent.vue');
          break;
        case 'settingsadmin':
          component = () => import('../components/settings/admin/AdminSettingsAppComponent.vue');
          break;
        case 'adminmailsettings':
          component = () => import('../components/settings/admin/AdminSettingsMailsComponent.vue');
          break;
        case 'weeksadmin':
          component = () => import('../components/settings/admin/AdminWeeksComponent.vue');
          break;
        case 'meetings':
          component = () => import('../components/meetings/admin/AdminMeetingsComponent.vue');
          break;
        case 'meetingscreate':
          component = () => import('../components/meetings/admin/AdminMeetingsAddComponent.vue');
          break;
        case 'statsadmin':
          component = () => import('../components/stats/admin/BookingStatsComponent.vue');
          break;

        // Auth Components
        case 'login':
          component = () => import('../components/auth/LoginComponent.vue');
          break;
        case 'changepassword':
          component = () => import('../components/auth/PasswordComponent.vue');
          break;
        case 'forgot-password':
          component = () => import('../components/auth/ForgotPasswordComponent.vue');
          break;

        // Front Page Components
        case 'rmlform':
          component = () => import('../components/front/RmlFormComponent.vue');
          break;
        case 'success':
          component = () => import('../components/front/SuccessComponent.vue');
          break;
        case 'experts':
        case 'experts2':
          component = () => import('../components/front/AssetsComponent.vue');
          break;
        case 'chats':
        case 'chatDetail':
          component = () => import('../components/chat/ChatsComponent.vue');
          break;
        case 'expert':
        case 'expertiframe':
          component = () => import('../components/front/AssetComponent.vue');
          break;
        case 'bookings':
          component = () => import('../components/bookings/BookingsComponent.vue');
          break;
        case 'bookingsdetail':
          component = () => import('../components/bookings/BookingDetailComponent.vue');
          break;
        case 'profile':
          component = () => import('../components/settings/ProfileComponent.vue');
          break;
        case 'settings':
          component = () => import('../components/settings/SettingsComponent.vue');
          break;
        case 'meetingdetail':
        case 'meetingdetail2':
          component = () => import('../components/meetings/MeetingDetailComponent.vue');
          break;

        default:
          component = null;
      }

      // Dynamically load and assign the component
      if (component) {
        this.currentComponent = (await component()).default;
      } else {
        this.currentComponent = null;
      }
    },
    async loadStaticComponents() {
      // Dynamically import static components
      this.topbarComponent = (await import('../components/_shared/TopbarComponent.vue')).default;
      this.headerComponent = (await import('../components/_shared/HeaderComponent.vue')).default;
      this.footerComponent = (await import('../components/_shared/FooterComponent.vue')).default;
    },
    checkIfInIframe() {
      this.isInIframe = window.self !== window.top;
      if (this.$route.name === 'success') {
        this.isInIframe = false;
      }

      if (this.isInIframe) {
        let appDefaultElements = document.querySelectorAll('.app-default');
        appDefaultElements.forEach((element) => {
          element.style.backgroundColor = 'white';
        });

        document.body.style.backgroundColor = 'white';
        document.body.classList.add('iframe');
      } else {
        if (localStorage.getItem('xpertiframe') && localStorage.getItem('xpertiframe') == 1) {
          if (this.$route.name != 'success' && this.$route.name != 'successiframe') {
            if(this.$route.query.customer) {
              localStorage.setItem('xpertcustomerselected', this.$route.query.customer);
              localStorage.setItem('xpertcustomertype', 1);
              localStorage.setItem('xpertiframe', 1);
              //localStorage.setItem('xperttoken', 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIxIiwianRpIjoiNDVmMzljODc0NWI0NTQ1NjI0NDEzMjNjOWYyMjAxNGZiY2QzMmU1MjUxMGRkNzM2YzcxMmY3YjJiOGJlMDJhMjAzMWM4MzdlOGQzZTEwNTYiLCJpYXQiOjE3Mjk0OTg4MTUuMjQxMDY4LCJuYmYiOjE3Mjk0OTg4MTUuMjQxMDcsImV4cCI6MTc2MTAzNDgxNS4yMzc2MjUsInN1YiI6IjEiLCJzY29wZXMiOltdfQ.TWm7_0dKHaOfhTkjIHMykt-QG18o6rYmyTR4PHVJ-Tqm5ETylPilT6oZdLF1wdboWxXWNr6hZTbIIpEQFzw6DE5TPlQNZAefpquvZuVBH8bz75JvKnwTlv2Qf38fqCMmJ95L35QALY_nxxV-QWm5ZUSdiqSO4cfTMHRx37f5LqGoYoy6TMIukDuLdcsA-FT6AHckEVhT_SslbyRlCeL8AOqdf3D5UfD1v9LdtLA8SlnJ6Rmx1nO-eWffva0MUHeSH73jiC50IxueSoSOJlWteLA-2G_WavHHkV75gcs7Aa7zS-YXeABC2Sc4vIzM6ThXy1NzMSZvkTVg0OjogNoTRKWTY4jEI7aJzs9bLcfpZSB9yGSiB4HDureTSL-H2XaCgGBfRUheqa48y-o3D6z-AucgD9CToA0tyuG3UR-nzcnUwlinFPoqrhjAcxbSUqHU_eawRrxO8KbrXTt1FTLFxSG3HI7Gr7IYlna01Ytc81MiKWOfeEOuBPsq_NOn_zvBAbqvAIAjtDXyThCquVRNtJsqsOj3DEHIbT1U_GvubuzlCqnBbi-xw8g2PNhvQxjwn5Zgof_NygXRlX-KwpozJKyWiBbBKdue8Rm-_MJMBGGXAnAKqPD_-A4cBSXfzfe4VDxA3blWcxaFv2HR1JfrfF3cDzYpY5fQfzbrZsQVi2c');
            } else {
              if (!localStorage.getItem('xpertcustomerselected') || localStorage.getItem('xpertcustomerselected') == null || localStorage.getItem('xpertcustomerselected') == '') {
                window.location.href = '/logout';
              }
            }
          }
        }
      }
    },
    handleRouteChange() {
      if (this.$route.query.customer) {
        localStorage.setItem('xpertcustomerselected', this.$route.query.customer);

        if (this.$route.name != 'success' && this.$route.name != 'experts' && this.$route.name != 'expertiframe' && this.$route.name != 'expert' && this.$route.name != 'rmlform') {
          //console.log('test');
        } else {
          localStorage.setItem('xpertcustomertype', 1);
          localStorage.setItem('xpertiframe', 1);
          //localStorage.setItem('xperttoken', 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIxIiwianRpIjoiNDVmMzljODc0NWI0NTQ1NjI0NDEzMjNjOWYyMjAxNGZiY2QzMmU1MjUxMGRkNzM2YzcxMmY3YjJiOGJlMDJhMjAzMWM4MzdlOGQzZTEwNTYiLCJpYXQiOjE3Mjk0OTg4MTUuMjQxMDY4LCJuYmYiOjE3Mjk0OTg4MTUuMjQxMDcsImV4cCI6MTc2MTAzNDgxNS4yMzc2MjUsInN1YiI6IjEiLCJzY29wZXMiOltdfQ.TWm7_0dKHaOfhTkjIHMykt-QG18o6rYmyTR4PHVJ-Tqm5ETylPilT6oZdLF1wdboWxXWNr6hZTbIIpEQFzw6DE5TPlQNZAefpquvZuVBH8bz75JvKnwTlv2Qf38fqCMmJ95L35QALY_nxxV-QWm5ZUSdiqSO4cfTMHRx37f5LqGoYoy6TMIukDuLdcsA-FT6AHckEVhT_SslbyRlCeL8AOqdf3D5UfD1v9LdtLA8SlnJ6Rmx1nO-eWffva0MUHeSH73jiC50IxueSoSOJlWteLA-2G_WavHHkV75gcs7Aa7zS-YXeABC2Sc4vIzM6ThXy1NzMSZvkTVg0OjogNoTRKWTY4jEI7aJzs9bLcfpZSB9yGSiB4HDureTSL-H2XaCgGBfRUheqa48y-o3D6z-AucgD9CToA0tyuG3UR-nzcnUwlinFPoqrhjAcxbSUqHU_eawRrxO8KbrXTt1FTLFxSG3HI7Gr7IYlna01Ytc81MiKWOfeEOuBPsq_NOn_zvBAbqvAIAjtDXyThCquVRNtJsqsOj3DEHIbT1U_GvubuzlCqnBbi-xw8g2PNhvQxjwn5Zgof_NygXRlX-KwpozJKyWiBbBKdue8Rm-_MJMBGGXAnAKqPD_-A4cBSXfzfe4VDxA3blWcxaFv2HR1JfrfF3cDzYpY5fQfzbrZsQVi2c');
        }

        if(this.$route.name != 'success' && this.$route.name != 'meetingdetail' && this.$route.name != 'meetingdetail2' && this.$route.name != 'rmlform') {
          window.location.href = '/experts';
        }
      } else {
        if (this.$route.name == 'logincheck') {
          if (!localStorage.getItem('xpertcustomerselected') || localStorage.getItem('xpertcustomerselected') == null || localStorage.getItem('xpertcustomerselected') == '') {
            if (this.$route.query.customer) {
              console.log('ok');
            } else {
              window.location.href = '/logout';
            }
          } else {
            window.location.href = '/experts';
          }
        }
      }

      if (this.$route.query.lang) {
        this.$i18n.locale = this.$route.query.lang;
        localStorage.setItem('selectedLanguage', this.$route.query.lang);
        console.log(this.$i18n.locale);
      }

      this.checkIfInIframe();
    },
  },
  mounted() {
    this.loadStaticComponents(); // Load static components asynchronously

    const baseUrl = window.location.origin.replace(/^https?:\/\//, '');

    const instance = axios.create({
        baseURL: this.$baseURL
    });

    instance.get('/customers')
      .then(response => {
        this.customers = response.data.customers;

        // Find the customer with a matching domain
        const matchedCustomer = this.customers.find(customer => customer.domain === baseUrl);

        if (matchedCustomer) {
          // If a match is found, alert and store the customer's ID
          if (!localStorage.getItem('xpertcustomerselected') || localStorage.getItem('xpertcustomerselected') != matchedCustomer.id) {
            localStorage.setItem('xpertcustomerselected', matchedCustomer.id);

            if (!localStorage.getItem('xpertcustomerselected')) {
              localStorage.setItem('xpertcustomertype', 1);
            }

            //Reload page
            if (window.location.pathname === '/') {
              window.location.href = '/experts';
            } else {
              window.location.reload();
            }
          }

          return; // Stops further execution
        } else {
          console.log('No match found');
        }
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      });

    if (!localStorage.getItem('xpertcustomerselected') || localStorage.getItem('xpertcustomerselected') == null || localStorage.getItem('xpertcustomerselected') == '') {
      const currentUrl = window.location.href;
      const containsLogin = currentUrl.includes('login');
      const containsLogout = currentUrl.includes('logout');
      const containsPassword = currentUrl.includes('password');

      if (containsLogin || containsLogout || containsPassword) {
        console.log('Xp');
      } else {
        if (this.$route.query.customer) {
          console.log('ok');
        } else {
          setTimeout(function() {
            if(!localStorage.getItem('xpertcustomerselected')) {
              window.location.href = '/logout';
            }
          }, 1000);
        }
      }
    }

    this.handleRouteChange();
  },
  beforeRouteUpdate(to, from, next) {
    this.handleRouteChange();
    next();
  },
};
</script>
